<template>
  <teleport to="body">
    <transition name="a-modal-fade">
      <div v-if="modelValue" class="a-modal" @click.self="onBackdropClick">
        <div class="a-modal__panel" :class="`a-modal__panel--${size}`" role="dialog" aria-modal="true">
          <header class="a-modal__head">
            <div class="a-modal__head-main">
              <h2 v-if="title" class="a-modal__title">
                <i v-if="icon" class="material-icons-round a-modal__title-icon">{{ icon }}</i>
                {{ title }}
              </h2>
              <p v-if="sub" class="a-modal__sub">{{ sub }}</p>
              <slot name="head" />
            </div>
            <button v-if="closable" type="button" class="a-modal__close" @click="close" title="닫기">
              <i class="material-icons-round">close</i>
            </button>
          </header>
          <div class="a-modal__body">
            <slot />
          </div>
          <footer v-if="$slots.foot" class="a-modal__foot">
            <slot name="foot" />
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'AModal',
  emits: ['update:modelValue', 'close'],
  props: {
    modelValue: { type: Boolean, required: true },
    title:    { type: String, default: '' },
    sub:      { type: String, default: '' },
    icon:     { type: String, default: '' },
    size:     {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg', 'xl', 'full'].includes(v),
    },
    closable: { type: Boolean, default: true },
    closeOnBackdrop: { type: Boolean, default: true },
  },
  watch: {
    modelValue(v) {
      if (typeof document === 'undefined') return;
      document.body.style.overflow = v ? 'hidden' : '';
    },
  },
  beforeUnmount() {
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    onBackdropClick() {
      if (this.closeOnBackdrop) this.close();
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/pineapple';

.a-modal {
  position: fixed;
  inset: 0;
  background: rgba(31, 27, 20, 0.45);
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.a-modal__panel {
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius-lg;
  box-shadow: 0 20px 60px rgba(31, 27, 20, 0.25);
  width: 100%;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &--sm  { max-width: 420px; }
  &--md  { max-width: 560px; }
  &--lg  { max-width: 780px; }
  &--xl  { max-width: 1080px; }
  &--full { max-width: calc(100vw - 48px); height: calc(100vh - 48px); }

  // Bootstrap residue cleanup (Checklist §5)
  :deep(.modal-content),
  :deep(.modal-dialog),
  :deep(.modal-body),
  :deep(.modal-header),
  :deep(.modal-footer) {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;
  }
}

.a-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid $p-line;
}

.a-modal__head-main {
  flex: 1;
  min-width: 0;
}

.a-modal__title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: $p-ink;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.a-modal__title-icon {
  font-size: 18px !important;
  color: $p-point-deep;
}

.a-modal__sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: $p-ink-mid;
}

.a-modal__close {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: $p-ink2;
  border-radius: $p-radius-sm;
  cursor: pointer;

  &:hover { background: $p-panel-alt; color: $p-ink; }
  i { font-size: 18px; }
}

.a-modal__body {
  padding: 20px;
  overflow: auto;
  flex: 1;
}

.a-modal__foot {
  padding: 14px 20px;
  border-top: 1px solid $p-line;
  background: $p-panel-alt;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  // Footer button height consistency (Checklist §5)
  > .a-btn,
  > button {
    height: $p-btn-h;
    line-height: 1;
    box-sizing: border-box;
  }
}

.a-modal-fade-enter-active,
.a-modal-fade-leave-active {
  transition: opacity 0.18s ease;
  .a-modal__panel { transition: transform 0.18s ease; }
}
.a-modal-fade-enter-from,
.a-modal-fade-leave-to {
  opacity: 0;
  .a-modal__panel { transform: translateY(6px) scale(0.99); }
}
</style>
